import { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import SEO from "@/components/SEO";
import ListingCard from "@/components/ListingCard";
import { ArrowRight, MapPin } from "lucide-react";

const CITIES = {
  paris: { name: "Paris", region: "Île-de-France", median_apt: 10500, median_house: 12000, blurb: "Marché parisien tendu où la vente à terme séduit vendeurs seniors et acheteurs sans crédit bancaire. Prix ferme et mensualités fixes rassurent des deux côtés." },
  lyon: { name: "Lyon", region: "Auvergne-Rhône-Alpes", median_apt: 4700, median_house: 5200, blurb: "Deuxième métropole française, Lyon combine dynamique économique et patrimoine haussmannien. La vente à terme y offre une alternative crédible au crédit classique." },
  marseille: { name: "Marseille", region: "Provence-Alpes-Côte d'Azur", median_apt: 3400, median_house: 4200, blurb: "Marseille présente une grande diversité de biens éligibles à la vente à terme : appartements, maisons de ville, bastides. Prix ferme, sans indexation." },
  bordeaux: { name: "Bordeaux", region: "Nouvelle-Aquitaine", median_apt: 4600, median_house: 5300, blurb: "Bordeaux, ville UNESCO, attire vendeurs seniors et jeunes acquéreurs. La vente à terme s'y développe comme alternative au viager." },
  toulouse: { name: "Toulouse", region: "Occitanie", median_apt: 3500, median_house: 4000, blurb: "La ville rose offre un marché immobilier accessible, propice à la vente à terme libre pour investisseurs et primo-accédants." },
  nantes: { name: "Nantes", region: "Pays de la Loire", median_apt: 3800, median_house: 4600, blurb: "Nantes séduit par sa qualité de vie et son dynamisme. La vente à terme y répond à une demande croissante de patrimoines diversifiés." },
  nice: { name: "Nice", region: "Provence-Alpes-Côte d'Azur", median_apt: 4700, median_house: 6200, blurb: "Sur la Côte d'Azur, la vente à terme occupée intéresse particulièrement les vendeurs seniors souhaitant conserver leur résidence." },
  montpellier: { name: "Montpellier", region: "Occitanie", median_apt: 3400, median_house: 4300, blurb: "Montpellier, ville étudiante et attractive, présente un marché diversifié où la vente à terme libre trouve des acheteurs sans crédit." },
};

export default function VilleVenteATerme() {
  const { city } = useParams();
  const info = CITIES[city?.toLowerCase()];
  const [listings, setListings] = useState([]);

  useEffect(() => {
    if (!info) return;
    api.get(`/listings?region=${encodeURIComponent(info.region)}&limit=6`).then(({ data }) => {
      setListings(data.filter((l) => l.city?.toLowerCase().includes(info.name.toLowerCase())).slice(0, 6));
    }).catch(() => {});
  }, [info, city]);

  const schema = useMemo(() => info ? ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `Vente à terme à ${info.name}`,
    "description": info.blurb,
    "url": `https://venteaterme.fr/vendre-vente-a-terme/${city}`,
  }) : null, [info, city]);

  if (!info) return <Navigate to="/vendre-en-vente-a-terme" replace />;

  return (
    <>
      <SEO
        title={`Vente à terme à ${info.name} — venteaterme.fr`}
        description={`Vendez ou achetez en vente à terme à ${info.name}. Prix ferme, mensualités fixes, sans intérêts. Prix médian et biens disponibles.`}
        schema={schema}
      />
      <section className="pt-24 pb-10 bg-navy text-cream">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-4 flex items-center gap-2">
            <MapPin size={14} /> {info.region}
          </div>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight">Vente à terme à {info.name}</h1>
          <p className="text-cream/85 mt-6 max-w-3xl">{info.blurb}</p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-navy/10 p-8">
            <div className="text-xs uppercase tracking-widest text-accent-brand font-bold mb-2">Prix médian appartements</div>
            <div className="font-serif text-4xl text-navy font-semibold">{eur(info.median_apt)}<span className="text-lg text-navy/50"> /m²</span></div>
          </div>
          <div className="bg-white border border-navy/10 p-8">
            <div className="text-xs uppercase tracking-widest text-accent-brand font-bold mb-2">Prix médian maisons</div>
            <div className="font-serif text-4xl text-navy font-semibold">{eur(info.median_house)}<span className="text-lg text-navy/50"> /m²</span></div>
          </div>
        </div>
        <p className="text-center text-xs text-navy/50 mt-4">Prix médians indicatifs. Sources publiques agrégées (DVF, notaires). Valeurs arrondies.</p>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-serif text-3xl text-navy">Biens disponibles à {info.name}</h2>
            <Link to="/annonces" className="text-sm font-bold uppercase tracking-widest text-navy hover:text-accent-brand">Voir toutes les annonces →</Link>
          </div>
          {listings.length === 0 ? (
            <div className="py-16 text-center text-navy/50 border border-navy/10">Aucune annonce à {info.name} pour le moment. Nous vous invitons à créer une alerte depuis la page annonces.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {listings.map((l) => <ListingCard key={l.id} l={l} />)}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-navy text-cream">
        <div className="max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif text-3xl md:text-4xl mb-4">Estimer votre bien à {info.name}&nbsp;?</h2>
          <p className="text-cream/80 mb-8">Obtenez une estimation gratuite en 2 minutes.</p>
          <Link to="/estimer" className="inline-flex items-center gap-2 bg-accent-brand text-white px-8 py-3 font-bold tracking-wide hover:bg-[#B08D22] transition-colors">
            Estimer mon bien <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
